import { ChangeEvent, KeyboardEvent, ReactNode } from "react";
import { ViewItem } from "../../../../Components/Views/ViewItem";
import { controllers } from "../../../../Controllers/Controllers";
import { navigator } from "../../../../Controllers/Navigator";
import { STATUS_LABELS, LeadCategoryType, LeadType } from "../../../../DataTypes/leads";
import { ResearchCandidateType } from "../../../../DataTypes/research";
import '../outreach-styles.css'
import '../Categories/categories-styles.css'
import '../Mail/mail-styles.css'
import '../../Settings/settings-styles.css'
import './research-styles.css'

type ResearchPageState = {
    keyword: string;
    location: string;
    categoryId: string;
    categories: LeadCategoryType[];

    results: ResearchCandidateType[];
    nextPageToken: string | null;
    searching: boolean;
    loadingMore: boolean;
    searchError: string | null;
    hasSearched: boolean;

    checkedPlaceIds: string[];
    addedLeadsByPlaceId: Record<string, LeadType>;
    focusedPlaceId: string | null;

    adding: boolean;
    addError: string | null;
    addMessage: string | null;

    botRunning: boolean;
    botMessage: string | null;
}

class ResearchPage extends ViewItem {
    state: ResearchPageState;

    constructor(props: any) {
        super(props)
        this.state = {
            keyword: '',
            location: '',
            categoryId: '',
            categories: [],

            results: [],
            nextPageToken: null,
            searching: false,
            loadingMore: false,
            searchError: null,
            hasSearched: false,

            checkedPlaceIds: [],
            addedLeadsByPlaceId: {},
            focusedPlaceId: null,

            adding: false,
            addError: null,
            addMessage: null,

            botRunning: false,
            botMessage: null,
        }
    }

    componentDidMount(): void {
        this.loadCategories()
    }

    loadCategories = async () => {
        const response = await controllers().outreach.getCategories()
        if (response && response.statusCode === 200) {
            this.setState({ categories: response.data?.categories || [] })
        }
    }

    handleKeywordChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ keyword: e.target.value })
    }

    handleLocationChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ location: e.target.value })
    }

    handleCategoryChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ categoryId: e.target.value })
    }

    handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') this.runSearch(false)
    }

    runSearch = async (loadMore: boolean) => {
        const { keyword, location, nextPageToken } = this.state
        if (!loadMore && !keyword.trim() && !location.trim()) {
            this.setState({ searchError: 'Enter a keyword and/or a location to search.' })
            return
        }
        if (loadMore && !nextPageToken) return

        this.setState(loadMore ? { loadingMore: true, searchError: null } : {
            searching: true,
            searchError: null,
            hasSearched: true,
            results: [],
            nextPageToken: null,
            checkedPlaceIds: [],
            focusedPlaceId: null,
        })

        const response = await controllers().research.search({
            keyword: keyword.trim() || undefined,
            location: location.trim() || undefined,
            page_token: loadMore ? (nextPageToken || undefined) : undefined,
        })

        if (!response || response.statusCode !== 200) {
            this.setState({ searching: false, loadingMore: false, searchError: response?.error || 'Search failed.' })
            return
        }

        const newResults: ResearchCandidateType[] = response.data?.results || []
        this.setState((prev: ResearchPageState) => ({
            searching: false,
            loadingMore: false,
            results: loadMore ? [...prev.results, ...newResults] : newResults,
            nextPageToken: response.data?.next_page_token || null,
        }))
    }

    toggleChecked = (placeId: string) => {
        this.setState((prev: ResearchPageState) => ({
            checkedPlaceIds: prev.checkedPlaceIds.includes(placeId)
                ? prev.checkedPlaceIds.filter(id => id !== placeId)
                : [...prev.checkedPlaceIds, placeId],
        }))
    }

    toggleSelectAll = () => {
        this.setState((prev: ResearchPageState) => {
            const selectable = prev.results.filter(r => !prev.addedLeadsByPlaceId[r.place_id]).map(r => r.place_id)
            const allSelected = selectable.length > 0 && selectable.every(id => prev.checkedPlaceIds.includes(id))
            return { checkedPlaceIds: allSelected ? [] : selectable }
        })
    }

    focusCandidate = (placeId: string) => {
        this.setState({ focusedPlaceId: placeId })
    }

    handleAddSelected = () => {
        this.handleAddPlaces(this.state.checkedPlaceIds)
    }

    handleAddSingle = (placeId: string) => {
        this.handleAddPlaces([placeId])
    }

    // The backend doesn't echo place_id back on the created lead — `created`
    // is just positionally aligned with the subset of placeIds that weren't
    // skipped, in the same order the loop processed them in. That's the only
    // way to know which candidate became which lead.
    handleAddPlaces = async (placeIds: string[]) => {
        if (!placeIds.length) return
        const { keyword, location, categoryId } = this.state
        this.setState({ adding: true, addError: null, addMessage: null })

        const query = [keyword.trim(), location.trim()].filter(Boolean).join(' in ')
        const response = await controllers().research.addLeads(
            placeIds,
            query,
            categoryId ? Number(categoryId) : undefined
        )

        if (!response || response.statusCode !== 200) {
            this.setState({ adding: false, addError: response?.error || 'Failed to add leads.' })
            return
        }

        const created: LeadType[] = response.data?.created || []
        const skipped: { place_id: string; reason: string }[] = response.data?.skipped || []
        const skippedIds = new Set(skipped.map(s => s.place_id))
        const succeededPlaceIds = placeIds.filter(id => !skippedIds.has(id))
        const newlyAdded: Record<string, LeadType> = {}
        succeededPlaceIds.forEach((placeId, i) => {
            if (created[i]) newlyAdded[placeId] = created[i]
        })

        this.setState((prev: ResearchPageState) => ({
            adding: false,
            addedLeadsByPlaceId: { ...prev.addedLeadsByPlaceId, ...newlyAdded },
            checkedPlaceIds: prev.checkedPlaceIds.filter(id => !newlyAdded[id]),
            addMessage: skipped.length
                ? `Added ${created.length} lead${created.length === 1 ? '' : 's'} — ${skipped.length} skipped.`
                : `Added ${created.length} lead${created.length === 1 ? '' : 's'}.`,
        }))
    }

    handleRunBot = async () => {
        this.setState({ botRunning: true, botMessage: null })
        const response = await controllers().research.runPrefetchBot()
        this.setState({
            botRunning: false,
            botMessage: response?.message || response?.error || 'Failed to start the prefetch bot.',
        })
    }

    renderStars = (rating: number | null) => {
        if (!rating) return null
        return <span className="research-rating">★ {rating.toFixed(1)}</span>
    }

    renderList = () => {
        const { results, checkedPlaceIds, addedLeadsByPlaceId, focusedPlaceId, searching } = this.state
        const selectable = results.filter(r => !addedLeadsByPlaceId[r.place_id])
        const allSelected = selectable.length > 0 && selectable.every(r => checkedPlaceIds.includes(r.place_id))

        return (
            <div className="mail-list-column">
                {results.length > 0 && (
                    <div className="research-select-all-row">
                        <label className="category-form-checkbox-row research-select-all-label">
                            <input type="checkbox" checked={allSelected} onChange={this.toggleSelectAll} disabled={!selectable.length} />
                            Select all
                        </label>
                        {checkedPlaceIds.length > 0 && (
                            <button className="outreach-add-btn" onClick={this.handleAddSelected} disabled={this.state.adding}>
                                {this.state.adding ? 'Adding...' : `Add ${checkedPlaceIds.length} as Leads`}
                            </button>
                        )}
                    </div>
                )}

                <div className="mail-list">
                    {results.map(candidate => {
                        const added = addedLeadsByPlaceId[candidate.place_id]
                        return (
                            <div
                                key={candidate.place_id}
                                className={`mail-list-item ${focusedPlaceId === candidate.place_id ? 'mail-list-item-active' : ''}`}
                                onClick={() => this.focusCandidate(candidate.place_id)}
                            >
                                <div className="mail-list-item-top">
                                    <span className="mail-list-item-sender">
                                        {!added && (
                                            <input
                                                type="checkbox"
                                                checked={checkedPlaceIds.includes(candidate.place_id)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => this.toggleChecked(candidate.place_id)}
                                            />
                                        )}
                                        {candidate.name}
                                    </span>
                                    {added && <span className="research-added-badge">✓ Added</span>}
                                </div>
                                <div className="mail-list-item-subject">{candidate.address || 'No address on file'}</div>
                                <div className="mail-list-item-snippet">
                                    {this.renderStars(candidate.rating)}
                                    {candidate.user_ratings_total ? ` (${candidate.user_ratings_total} reviews)` : ''}
                                </div>
                            </div>
                        )
                    })}
                </div>

                {this.state.nextPageToken && (
                    <button
                        className="outreach-pagination-btn research-load-more"
                        onClick={() => this.runSearch(true)}
                        disabled={this.state.loadingMore || searching}
                    >
                        {this.state.loadingMore ? 'Loading...' : 'Load more'}
                    </button>
                )}
            </div>
        )
    }

    renderDetail = () => {
        const { results, focusedPlaceId, addedLeadsByPlaceId, adding } = this.state
        if (!focusedPlaceId) {
            return (
                <div className="mail-thread-panel mail-thread-empty">
                    <span className="outreach-muted">Select a company to see details.</span>
                </div>
            )
        }

        const candidate = results.find(r => r.place_id === focusedPlaceId)
        if (!candidate) return null
        const addedLead = addedLeadsByPlaceId[focusedPlaceId]

        return (
            <div className="mail-thread-panel">
                <div className="mail-thread-header">
                    <div className="mail-thread-header-info">
                        <h2 className="mail-thread-subject">{candidate.name}</h2>
                        <p className="mail-thread-participants">{candidate.address || 'No address on file'}</p>
                    </div>
                    {addedLead && <span className="research-added-badge research-added-badge-large">✓ Added as Lead</span>}
                </div>

                {!addedLead && (
                    <>
                        <div className="research-detail-facts">
                            {candidate.rating != null && (
                                <div className="research-detail-fact">
                                    <span className="settings-signature-preview-label">Rating</span>
                                    <p>{this.renderStars(candidate.rating)} {candidate.user_ratings_total ? `(${candidate.user_ratings_total} reviews)` : ''}</p>
                                </div>
                            )}
                            {candidate.business_status && (
                                <div className="research-detail-fact">
                                    <span className="settings-signature-preview-label">Status</span>
                                    <p>{candidate.business_status.replace(/_/g, ' ')}</p>
                                </div>
                            )}
                        </div>

                        {candidate.types.length > 0 && (
                            <div className="research-type-chips">
                                {candidate.types.map(type => (
                                    <span key={type} className="research-type-chip">{type.replace(/_/g, ' ')}</span>
                                ))}
                            </div>
                        )}

                        <div className="settings-email-actions research-detail-actions">
                            <button
                                className="outreach-add-btn"
                                onClick={() => this.handleAddSingle(candidate.place_id)}
                                disabled={adding}
                            >
                                {adding ? 'Adding...' : 'Add as Lead'}
                            </button>
                        </div>
                    </>
                )}

                {addedLead && (
                    <>
                        <div className="research-detail-facts">
                            <div className="research-detail-fact">
                                <span className="settings-signature-preview-label">Status</span>
                                <p>
                                    <span className={`research-status-chip lead-status-${addedLead.status}`}>
                                        {STATUS_LABELS[addedLead.status] || addedLead.status}
                                    </span>
                                </p>
                            </div>
                            {addedLead.company_website_url && (
                                <div className="research-detail-fact">
                                    <span className="settings-signature-preview-label">Website</span>
                                    <p>
                                        <a href={addedLead.company_website_url} target="_blank" rel="noopener noreferrer">
                                            {addedLead.company_website_url}
                                        </a>
                                    </p>
                                </div>
                            )}
                        </div>

                        {addedLead.notes && (
                            <div className="research-detail-fact">
                                <span className="settings-signature-preview-label">Notes</span>
                                <p className="research-detail-notes">{addedLead.notes}</p>
                            </div>
                        )}

                        <div className="settings-email-actions research-detail-actions">
                            <button className="outreach-edit-btn" onClick={() => navigator().navigate(`/outreach/${addedLead.id}/edit`)}>
                                Edit Lead
                            </button>
                        </div>
                    </>
                )}
            </div>
        )
    }

    render(): ReactNode {
        const {
            keyword, location, categoryId, categories,
            results, searching, searchError, hasSearched,
            addError, addMessage, botRunning, botMessage,
        } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Research</h1>
                        <p className="outreach-subtitle">Find new companies and add them straight into your pipeline.</p>
                    </div>

                    <div className="research-toolbar">
                        <input
                            type="text"
                            className="outreach-search research-toolbar-field"
                            placeholder="Keyword, e.g. plumbers"
                            value={keyword}
                            onChange={this.handleKeywordChange}
                            onKeyDown={this.handleSearchKeyDown}
                        />
                        <input
                            type="text"
                            className="outreach-search research-toolbar-field"
                            placeholder="Location, e.g. Austin, TX"
                            value={location}
                            onChange={this.handleLocationChange}
                            onKeyDown={this.handleSearchKeyDown}
                        />
                        <select className="outreach-status-select" value={categoryId} onChange={this.handleCategoryChange}>
                            <option value="">No category</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <button className="outreach-add-btn" onClick={() => this.runSearch(false)} disabled={searching}>
                            {searching ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                    <button className="outreach-edit-btn" onClick={this.handleRunBot} disabled={botRunning}>
                        {botRunning ? 'Starting...' : 'Run Prefetch Bot'}
                    </button>
                </div>

                {botMessage && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">{botMessage}</span>
                        <button className="outreach-banner-action" onClick={() => this.setState({ botMessage: null })}>
                            Dismiss
                        </button>
                    </div>
                )}

                {addError && <div className="category-form-error">{addError}</div>}
                {addMessage && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">{addMessage}</span>
                    </div>
                )}

                {searchError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{searchError}</span>
                    </div>
                )}

                {searching && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!searching && hasSearched && !searchError && results.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No companies found — try a different keyword or location.
                    </div>
                )}

                {!searching && !hasSearched && (
                    <div className="outreach-state outreach-empty">
                        Search for a keyword and/or location to find companies.
                    </div>
                )}

                {!searching && results.length > 0 && (
                    <div className="mail-layout">
                        {this.renderList()}
                        {this.renderDetail()}
                    </div>
                )}
            </div>
        )
    }
}

export { ResearchPage }
